import { useEffect, useRef } from "react";

// The sliver of Google Identity Services we use.
type GoogleIdentityServices = {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: { credential?: string }) => void;
      }) => void;
      renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
    };
  };
};

const googleIdentity = () =>
  (window as unknown as { google?: GoogleIdentityServices }).google;

// Google's own cap; anything wider is ignored, so we render at the cap and
// scale the (invisible) result up to our button's real width.
const GOOGLE_RENDER_WIDTH = 400;

type Options = {
  /** Unset (no REACT_APP_GOOGLE_CLIENT_ID) keeps the whole feature inert. */
  clientId?: string;
  /** False on steps where the button is not on screen. */
  enabled: boolean;
  /** "signin_with" on the login page, "signup_with" on registration. */
  text?: "signin_with" | "signup_with";
  onCredential: (credential?: string) => void;
};

/**
 * Renders Google's own sign-in button into the returned ref, invisible, and
 * stretched over whatever button the page draws in its place.
 *
 * Google renders and styles that button itself and will not draw it wider than
 * 400px, so a page that wants it to match its own full-width buttons has to
 * keep the real one transparent and take the clicks through it.
 *
 * The page's callback is read through a ref because GIS keeps the function it
 * was initialized with — a plain closure would go on seeing the first render's
 * state forever.
 */
export function useGoogleIdentityButton({
  clientId,
  enabled,
  text = "signin_with",
  onCredential,
}: Options) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const callbackRef = useRef(onCredential);
  callbackRef.current = onCredential;

  useEffect(() => {
    if (!clientId || !enabled) return;

    let cancelled = false;

    const stretchOverOurButton = () => {
      const host = hostRef.current;
      const inner = host?.firstElementChild as HTMLElement | null;
      if (!host || !inner) return;
      const renderedWidth = inner.offsetWidth || GOOGLE_RENDER_WIDTH;
      const renderedHeight = inner.offsetHeight || 44;
      inner.style.transformOrigin = "top left";
      inner.style.transform = `scale(${host.offsetWidth / renderedWidth}, ${host.offsetHeight / renderedHeight})`;
    };

    const renderButton = () => {
      const google = googleIdentity();
      if (cancelled || !google?.accounts?.id || !hostRef.current) return;
      google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => callbackRef.current(response?.credential),
      });
      hostRef.current.innerHTML = "";
      google.accounts.id.renderButton(hostRef.current, {
        theme: "outline",
        size: "large",
        width: GOOGLE_RENDER_WIDTH,
        text,
        shape: "pill",
      });
      // The button's own markup lands a frame later, so measure after paint.
      requestAnimationFrame(stretchOverOurButton);
      window.addEventListener("resize", stretchOverOurButton);
    };

    if (googleIdentity()?.accounts?.id) {
      renderButton();
      return () => {
        cancelled = true;
        window.removeEventListener("resize", stretchOverOurButton);
      };
    }

    // Loaded here rather than in index.html so that with no client id
    // configured nothing third-party is ever fetched.
    const existing = document.getElementById(
      "google-identity-services"
    ) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", renderButton);
      return () => {
        cancelled = true;
        existing.removeEventListener("load", renderButton);
        window.removeEventListener("resize", stretchOverOurButton);
      };
    }

    const script = document.createElement("script");
    script.id = "google-identity-services";
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = renderButton;
    document.body.appendChild(script);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", stretchOverOurButton);
    };
  }, [clientId, enabled, text]);

  return hostRef;
}
